import { useEffect, useState } from 'react';
import { useOrders } from '../store/orderStore';
import { api, AdminOrder } from '../lib/api';
import { getAccessToken } from '../lib/token';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'completed', label: 'Completed' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'cancelled', label: 'Cancelled' },
];

const REFUNDABLE_STATUSES = ['paid', 'completed', 'fulfilled'];

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function formatMoney(amount: number, currency?: string): string {
  const code = (currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

// What the customer actually paid: net + VAT in the checkout currency.
function orderTotal(order: AdminOrder): number {
  return order.display_total ?? order.total_amount;
}

async function download(url: string, isCsv: boolean) {
  try {
    const token = getAccessToken();
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Export failed');
    const blob = isCsv
      ? new Blob([await res.text()], { type: 'text/csv;charset=utf-8' })
      : await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `orders-${new Date().toISOString().slice(0, 10)}.${isCsv ? 'csv' : 'pdf'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (e) {
    toast.error((e as Error).message);
  }
}

function Orders() {
  const {
    orders,
    total,
    isLoading,
    error,
    statusFilter,
    searchQuery,
    setStatusFilter,
    setSearchQuery,
    fetchOrders,
    refundOrder,
  } = useOrders();

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);
  const [refundingId, setRefundingId] = useState<number | null>(null);

  const isRefundable = (order: AdminOrder): boolean =>
    REFUNDABLE_STATUSES.includes(order.status) && !order.refund_status && orderTotal(order) > 0;

  const handleRefund = async (order: AdminOrder) => {
    const confirmed = window.confirm(
      `Refund order #${order.id} for ${formatMoney(orderTotal(order), order.currency)}?\n\n` +
        'The customer will lose download access to the products in this order.'
    );
    if (!confirmed) return;
    setRefundingId(order.id);
    try {
      await refundOrder(order.id);
      toast.success(`Order #${order.id} refunded`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRefundingId(null);
    }
  };

  useEffect(() => {
    fetchOrders().catch((e: Error) => toast.error(e.message));
  }, [fetchOrders, statusFilter, searchQuery]);

  const handleExport = (kind: 'csv' | 'pdf') => {
    setExporting(kind);
    download(
      kind === 'csv' ? api.orders.exportCsvUrl() : api.orders.exportPdfUrl(),
      kind === 'csv'
    );
    setTimeout(() => setExporting(null), 800);
  };

  return (
    <div className="container">
      <h1 className="page-title">Orders</h1>
      <p className="page-subtitle">
        {total} order{total === 1 ? '' : 's'}.
      </p>

      <div className="toolbar">
        <input
          type="text"
          placeholder="Search by buyer or product…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearchQuery(searchInput.trim());
          }}
        />
        <select
          value={statusFilter || ''}
          onChange={(e) => setStatusFilter(e.target.value || null)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => setSearchQuery(searchInput.trim())}
        >
          Search
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn--outline"
            disabled={exporting !== null}
            onClick={() => handleExport('csv')}
          >
            {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            type="button"
            className="btn btn--outline"
            disabled={exporting !== null}
            onClick={() => handleExport('pdf')}
          >
            {exporting === 'pdf' ? 'Exporting…' : 'Export PDF'}
          </button>
        </div>
      </div>

      {error && <p className="form-error">{error}</p>}

      {isLoading && orders.length === 0 ? (
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      ) : orders.length === 0 ? (
        <div className="panel">
          <p className="empty-state">No orders found.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Buyer</th>
                <th>Email</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>#{order.id}</td>
                  <td>{order.user_name}</td>
                  <td>{order.email}</td>
                  <td>
                    {order.items?.map((item) => (
                      <div key={item.id}>
                        {item.product_name} × {item.quantity}
                      </div>
                    ))}
                  </td>
                  <td>
                    {formatMoney(orderTotal(order), order.currency)}
                    {order.tax_amount ? (
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        incl. {formatMoney(order.tax_amount, order.currency)} VAT
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <span className={`status-badge status--${order.status}`}>{order.status}</span>
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    {isRefundable(order) ? (
                      <button
                        type="button"
                        className="btn btn--outline btn--sm"
                        disabled={refundingId === order.id}
                        onClick={() => handleRefund(order)}
                      >
                        {refundingId === order.id ? 'Refunding…' : 'Refund'}
                      </button>
                    ) : order.refund_status && order.refund_status !== 'succeeded' ? (
                      <span className="text-muted">Refund {order.refund_status}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Orders;
